import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  Card,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import _ from 'lodash';

import { useAppSelector } from '../../../../../../hooks';
import { getAuthState } from '../../../../../../store/AuthSlice';
import {
  adminDeleteCourseTemplate,
  getCourseTemplatesAsAdmin,
  getCourseTemplatesState,
  ICourseTemplate,
  resetAdminCourseTemplatesData,
  TGetCourseTemplatesAsAdmin,
} from '../../../../../../store/V2/CourseTemplatesSlice';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../../../components/V2/Table/type1/TableHeadCustom';
import CourseTemplateTableRow from './components/CourseTemplateTableRow';
import TableNoData from '../../../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../../../components/Loaders/TableLoader';
import TablePaginationActions from '../../../../../../components/V2/Table/type1/TablePaginationActions';
import {
  defaultTextFieldProps,
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import EditCourseTemplateModal from './modals/EditCourseTemplateModal';
import CourseListDropdown, {
  TCourseSelectOption,
} from '../../../../../../components/Inputs/admin/CourseListDropdown';
import {
  getCurriculumsV2,
  getCurriculumV2State,
} from '../../../../../../store/V2/CurriculumSlice';
import { ISelectedOption } from '../../../../../../constants';

type TState = {
  searchText: string;
  selectedCourse: TCourseSelectOption | null;
  selectedCurriculum: ISelectedOption | null;
  selectedSubject: ISelectedOption | null;
  selectedGrade: ISelectedOption | null;
};

type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  searchText?: string;
  courseId?: string;
  curriculum?: string;
  subject?: string;
  grade?: string;
};

const tableCardContainerStyles: SxProps = {
  borderRadius: '20px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
};

const defaultListLimit = 30;
const defaultOrderBy = 'createdAt';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const defaultFilterData: TFilterData = {
  limit: defaultListLimit,
  offset: 0,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
};

const initialState: TState = {
  searchText: '',
  selectedCourse: null,
  selectedCurriculum: null,
  selectedSubject: null,
  selectedGrade: null,
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'createdAt',
    label: 'Created At',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'name',
    label: 'Name',
    sortable: true,
    minWidth: 250,
    maxWidth: 250,
  },
  {
    id: 'course',
    label: 'Course',
    sortable: true,
    minWidth: 200,
    maxWidth: 200,
  },
  {
    id: 'curriculum',
    label: 'Curriculum',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'subject',
    label: 'Subject',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'grade',
    label: 'Grade',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'isTrialTemplate',
    label: 'Trial Template',
    sortable: true,
    minWidth: 100,
    maxWidth: 100,
  },
  {
    id: 'createdBy',
    label: 'Created By',
    sortable: false,
    minWidth: 150,
    maxWidth: 150,
  },
  {
    id: 'lastUpdatedBy',
    label: 'Updated By',
    sortable: false,
    minWidth: 150,
    maxWidth: 150,
  },
  {
    id: 'actions',
    label: 'Actions',
    sortable: false,
    width: 100,
    minWidth: 100,
  },
];

const CourseTemplatesTab: FunctionComponent = () => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  // Store
  const authState = useAppSelector(getAuthState);
  const curriculumState = useAppSelector(getCurriculumV2State);
  const coursePricingTemplatesState = useAppSelector(getCourseTemplatesState);

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState<TState>({ ...initialState });
  const [isActionsDisabled, setActionsDisabled] = useState(false);
  const [filterData, setFilterData] = useState({ ...defaultFilterData });
  const [editTemplateModalData, setEditTemplateModalData] = useState<{
    open: boolean;
    template?: ICourseTemplate;
  }>({
    open: false,
  });

  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<ISelectedOption>
  >([]);

  const [subjectOptions, setSubjectOptions] = useState<Array<ISelectedOption>>(
    []
  );

  const [gradeOptions, setGradeOptions] = useState<Array<ISelectedOption>>([]);

  const { adminCourseTemplatesData } = coursePricingTemplatesState;

  const authUser = authState.user;

  let courseTemplatesList: ICourseTemplate[] = [];
  let totalCount = 0;

  if (adminCourseTemplatesData) {
    totalCount = adminCourseTemplatesData.totalDocs || 0;

    if (adminCourseTemplatesData.list && adminCourseTemplatesData.list.length) {
      courseTemplatesList = adminCourseTemplatesData.list;
    }
  }

  useEffect(() => {
    dispatch(getCurriculumsV2());
    getCourseTemplatesList({ ...filterData });

    return () => {
      dispatch(resetAdminCourseTemplatesData());
    };
  }, []);

  useEffect(() => {
    if (curriculumState.list) {
      setCurriculumOptions(
        _.uniq(
          (curriculumState.list || []).map((curriculumItem) => ({
            label: curriculumItem.curriculum,
            value: curriculumItem.curriculum,
          }))
        )
      );
    }
  }, [curriculumState.list]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const getCourseTemplatesList = (filterDataAttributes: TFilterData) => {
    const apiData: TGetCourseTemplatesAsAdmin = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.searchText) {
      apiData.searchText = filterDataAttributes.searchText;
    }

    if (filterDataAttributes.courseId) {
      apiData.courseId = filterDataAttributes.courseId;
    }

    if (filterDataAttributes.curriculum) {
      apiData.curriculum = filterDataAttributes.curriculum;
    }

    if (filterDataAttributes.subject) {
      apiData.subject = filterDataAttributes.subject;
    }

    if (filterDataAttributes.grade) {
      apiData.grade = filterDataAttributes.grade;
    }

    dispatch(getCourseTemplatesAsAdmin({ ...apiData }));
    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getCourseTemplatesList({
      ...filterData,
      sortValue: newSortValue,
      sortKey: id,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getCourseTemplatesList({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getCourseTemplatesList({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  const handleSearch = () => {
    getCourseTemplatesList({
      ...filterData,
      searchText: state.searchText.trim().length ? state.searchText : undefined,
    });
  };

  const handleDelete = async (template: ICourseTemplate) => {
    await confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this course template?',
    }).then(async () => {
      setActionsDisabled(true);

      await dispatch(adminDeleteCourseTemplate(template._id));

      setActionsDisabled(false);
    });
  };

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption | null
  ) => {
    const newFilterData: TFilterData = {
      ...filterData,
      curriculum: value ? value.value : undefined,
      subject: undefined,
      grade: undefined,
    };

    setFilterData(newFilterData);
    getCourseTemplatesList({ ...newFilterData });
    updateStateAttributes({
      selectedCurriculum: value,
      selectedSubject: null,
      selectedGrade: null,
    });

    let newSubjectOptions: ISelectedOption[] = [];

    if (curriculumState.list && curriculumState.list.length && value) {
      const matchingCurriculum = curriculumState.list.find(
        (item) => item.curriculum === value.value
      );

      if (matchingCurriculum) {
        const subjects = matchingCurriculum.subjects || [];

        newSubjectOptions = _.uniq(
          subjects.map((item) => ({
            label: item.subject,
            value: item.subject,
          }))
        );
      }
    }

    setSubjectOptions(newSubjectOptions);
    setGradeOptions([]);
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption | null
  ) => {
    const newFilterData: TFilterData = {
      ...filterData,
      subject: value ? value.value : undefined,
      grade: undefined,
    };

    setFilterData(newFilterData);
    getCourseTemplatesList({ ...newFilterData });
    updateStateAttributes({
      selectedSubject: value,
      selectedGrade: null,
    });

    let newGradeOptions: ISelectedOption[] = [];

    if (
      curriculumState.list &&
      curriculumState.list.length &&
      state.selectedCurriculum
    ) {
      const matchingCurriculum = curriculumState.list.find(
        (item) => item.curriculum === state.selectedCurriculum?.value
      );

      if (matchingCurriculum && value) {
        const subjects = matchingCurriculum.subjects || [];

        const matchingSubject = subjects.find(
          (item) => item.subject === value.value
        );

        if (matchingSubject) {
          const grades = matchingSubject.grade || [];

          newGradeOptions = _.uniq(
            grades.map((item) => ({ label: item.grade, value: item.grade }))
          );
        }
      }
    }

    setGradeOptions(newGradeOptions);
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption | null
  ) => {
    const newFilterData: TFilterData = {
      ...filterData,
      grade: value ? value.value : undefined,
    };

    setFilterData(newFilterData);
    getCourseTemplatesList({ ...newFilterData });
    updateStateAttributes({
      selectedGrade: value,
    });
  };

  return (
    <Fade in>
      <div>
        <Card sx={tableCardContainerStyles}>
          <div style={{ padding: '20px' }}>
            <div>
              <Typography fontSize="24px" fontWeight={600}>
                Course Templates
              </Typography>
            </div>
          </div>
          <div style={{ padding: '0 20px 20px 20px' }}>
            <Grid
              container
              columnSpacing="16px"
              rowSpacing="20px"
              alignItems="center"
            >
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <CourseListDropdown
                    selectedItem={state.selectedCourse}
                    handleChange={(e, value) => {
                      updateStateAttributes({
                        selectedCourse: value,
                      });

                      getCourseTemplatesList({
                        ...filterData,
                        courseId: value ? value.value : undefined,
                      });
                    }}
                    textFieldProps={{
                      label: 'Course',
                      placeholder: 'Select',
                      ...defaultTextFieldProps,
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Autocomplete
                    size="small"
                    value={state.selectedCurriculum}
                    options={curriculumOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    getOptionKey={(option) => option.value}
                    isOptionEqualToValue={(option, value) =>
                      option.label === value.value
                    }
                    onChange={handleCurriculumChange}
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...defaultTextFieldProps}
                        size="small"
                        label="Curriculum"
                        placeholder="Select"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Autocomplete
                    size="small"
                    disabled={!state.selectedCurriculum}
                    value={state.selectedSubject}
                    options={subjectOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    getOptionKey={(option) => option.value}
                    isOptionEqualToValue={(option, value) =>
                      option.label === value.value
                    }
                    onChange={handleSubjectChange}
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...defaultTextFieldProps}
                        size="small"
                        label="Subject"
                        placeholder="Select"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Autocomplete
                    size="small"
                    disabled={!state.selectedSubject}
                    value={state.selectedGrade}
                    options={gradeOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    getOptionKey={(option) => option.value}
                    onChange={handleGradeChange}
                    isOptionEqualToValue={(option, value) =>
                      option.label === value.value
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...defaultTextFieldProps}
                        size="small"
                        label="Grade"
                        placeholder="Select"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <Stack direction="row" alignItems="center" gap="20px">
                  <TPTextField
                    fullWidth
                    {...defaultTextFieldProps}
                    label="Search by name"
                    placeholder="Enter here..."
                    value={state.searchText}
                    onChange={(event) =>
                      updateStateAttributes({
                        searchText: event.target.value,
                      })
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        !(
                          !state.searchText.trim().length &&
                          !filterData.searchText?.trim().length
                        )
                      ) {
                        handleSearch();
                      }
                    }}
                  />
                  <DarkButton
                    variant="contained"
                    color="inherit"
                    sx={{ ...defaultButtonStyles }}
                    onClick={handleSearch}
                    disabled={
                      (!state.searchText.trim().length &&
                        !filterData.searchText?.trim().length) ||
                      coursePricingTemplatesState.status === 'loading'
                    }
                  >
                    Search
                  </DarkButton>
                </Stack>
              </Grid>
            </Grid>
          </div>
          <div style={{ paddingBottom: '20px' }}>
            <TableContainer
              sx={{ position: 'relative', maxHeight: 'calc(100vh - 250px)' }}
            >
              <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={tableData.order}
                  orderBy={tableData.orderBy}
                  headLabels={tableHeaderLabels}
                  rowCount={0}
                  numSelected={tableData.selected.length}
                  onSort={handleSort}
                />
                <TableBody>
                  {courseTemplatesList.map((row) => {
                    return (
                      <CourseTemplateTableRow
                        key={row._id}
                        courseTemplate={row}
                        isActionsDisabled={isActionsDisabled}
                        onDelete={() => handleDelete(row)}
                        onUpdate={() =>
                          setEditTemplateModalData({
                            open: true,
                            template: row,
                          })
                        }
                      />
                    );
                  })}
                  {coursePricingTemplatesState.status !== 'loading' &&
                  !courseTemplatesList.length ? (
                    <TableNoData title="No templates found" />
                  ) : null}
                  {coursePricingTemplatesState.status === 'loading' &&
                  !courseTemplatesList.length ? (
                    <TableLoader />
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ position: 'relative' }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={tableData.page}
                rowsPerPage={tableData.rowsPerPage}
                rowsPerPageOptions={defaultRowsPerPageOptions}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleRowsPerPageChange}
                ActionsComponent={TablePaginationActions}
              />

              <FormControlLabel
                label="Dense"
                sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                control={
                  <Switch
                    checked={tableData.dense}
                    onChange={tableData.onChangeDense}
                  />
                }
              />
            </Box>
          </div>
        </Card>
        {/* Modals */}
        {editTemplateModalData.open && editTemplateModalData.template ? (
          <EditCourseTemplateModal
            open={editTemplateModalData.open}
            courseTemplate={editTemplateModalData.template}
            onClose={() =>
              setEditTemplateModalData({
                open: false,
              })
            }
          />
        ) : null}
      </div>
    </Fade>
  );
};

export default CourseTemplatesTab;
